import React, { useEffect, useState } from "react";
import { Wrench, Check, X, MapPin, ShieldCheck, Wallet, TrendingUp, Users, Menu, Loader2, Save, Download, FileText, Headphones, ShieldAlert, ClipboardCheck, GripVertical, MessageSquare, Send, Inbox, KeyRound } from "lucide-react";
import { ActivationFunnel } from "../../components/onboarding/ActivationFunnel";
import { AdminHelpVideos, AdminOnboardingEmails, AdminEmailTest } from "../../components/onboarding/AdminOnboarding";
import { AdminBlogCovers } from "../../components/brand/AdminBlogCovers";
import { AdminBlogArticles } from "../../components/brand/AdminBlogArticles";
import api from "../../lib/api";
import { toast } from "sonner";
import { CommunesManager } from "../../components/dashboard/CommunesManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

function SupportSettings() {
  const [email, setEmail] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/support-email").then((r) => { setEmail(r.data.support_email || ""); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/support-email", { support_email: email }); toast.success("Adresse de support enregistrée"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="support-settings">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Headphones className="w-5 h-5" /> Support & Assistance</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <label className="text-sm font-medium text-brand-green/70 mb-1.5 block">Adresse email recevant les transferts « Parler à un humain »</label>
        <div className="flex flex-wrap gap-3">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!loaded} placeholder="support@360immoinvest.com" className="flex-1 min-w-[240px] px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm text-brand-greenDeep" data-testid="support-email-input" />
          <button onClick={save} disabled={saving || !email} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="save-support-email-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
        <p className="text-xs text-brand-green/45 mt-2">Les messages envoyés depuis l'Assistant IA (« Parler à un humain ») seront adressés à cette adresse.</p>
      </div>
    </div>
  );
}

const CAT_LABEL = {
  probleme: "Problème signalé", evolution: "Évolution / amélioration",
  partenariat: "Partenariat", divers: "Divers",
};

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm";
  const submit = async () => {
    if (nw.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (nw !== confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Mot de passe mis à jour");
      setCur(""); setNw(""); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6" data-testid="change-password-section">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Changer mon mot de passe</h3>
      <p className="text-xs text-brand-green/55 mb-4">Saisissez votre mot de passe actuel puis le nouveau (8 caractères minimum).</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Mot de passe actuel" autoComplete="current-password" className={inp} data-testid="cp-current" />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Nouveau mot de passe" autoComplete="new-password" className={inp} data-testid="cp-new" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmer le nouveau" autoComplete="new-password" className={inp} data-testid="cp-confirm" />
      </div>
      <button onClick={submit} disabled={busy || !cur || !nw || !confirm} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="cp-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Mettre à jour
      </button>
    </div>
  );
}


function ContactInbox() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/contact-threads").then((r) => setThreads(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const active = threads.find((t) => t.id === activeId);
  const send = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/contact-threads/${active.id}/reply`, { text: reply });
      setThreads((prev) => prev.map((t) => t.id === active.id ? { ...t, messages: [...(t.messages || []), r.data], admin_unread: 0 } : t));
      setReply("");
      toast.success("Réponse envoyée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const totalUnread = threads.reduce((n, t) => n + (t.admin_unread || 0), 0);
  return (
    <div data-testid="admin-contact-inbox">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2">
        <Inbox className="w-5 h-5" /> Messages de contact {totalUnread > 0 && <span className="px-2 py-0.5 rounded-full bg-brand-limeDark text-brand-greenDeep text-xs font-bold">{totalUnread}</span>}
      </h3>
      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden grid md:grid-cols-3 min-h-[320px]">
        <div className="border-r border-brand-soft max-h-[420px] overflow-y-auto" data-testid="contact-thread-list">
          {threads.length === 0 && <p className="p-5 text-xs text-brand-green/45">Aucun message pour le moment.</p>}
          {threads.map((t) => {
            const last = (t.messages || [])[t.messages.length - 1];
            return (
              <button key={t.id} onClick={() => setActiveId(t.id)} data-testid={`contact-thread-${t.id}`}
                className={`w-full text-left px-4 py-3 border-b border-brand-soft transition-colors ${activeId === t.id ? "bg-brand-softer" : "hover:bg-brand-softer/50"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-brand-greenDeep truncate">{t.user_name || t.user_email || "Utilisateur"}</p>
                  {t.admin_unread > 0 && <span className="w-5 h-5 rounded-full bg-brand-limeDark text-brand-greenDeep text-xs font-bold flex items-center justify-center">{t.admin_unread}</span>}
                </div>
                <p className="text-[11px] text-brand-green/50">{t.user_role} · {CAT_LABEL[t.last_category] || "Divers"}</p>
                <p className="text-xs text-brand-green/50 truncate mt-0.5">{last?.text}</p>
              </button>
            );
          })}
        </div>
        <div className="md:col-span-2 flex flex-col">
          {active ? (
            <>
              <div className="px-5 py-3 border-b border-brand-soft">
                <p className="font-semibold text-brand-greenDeep text-sm">{active.user_name || active.user_email}</p>
                <p className="text-xs text-brand-green/50">{active.user_email} · {active.user_role}</p>
              </div>
              <div className="flex-1 p-5 space-y-3 max-h-[300px] overflow-y-auto bg-brand-softer/30" data-testid="contact-thread-messages">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.from === "admin" ? "bg-brand-lime text-brand-greenDeep" : "bg-white border border-brand-soft text-brand-green"}`}>
                      {m.category_label && m.from !== "admin" && <p className="text-[10px] font-semibold text-brand-limeDark mb-0.5">{m.category_label}</p>}
                      {m.attachment && (
                        m.attachment.is_image ? (
                          <a href={fileUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`contact-attach-img-${i}`}>
                            <img src={fileUrl(m.attachment.url)} alt={m.attachment.filename} className="rounded-lg max-h-44 mb-1 border border-brand-soft/60" />
                          </a>
                        ) : (
                          <a href={fileUrl(m.attachment.url)} target="_blank" rel="noreferrer" data-testid={`contact-attach-file-${i}`}
                            className="flex items-center gap-2 mb-1 px-3 py-2 rounded-lg bg-white/70 border border-brand-soft text-brand-greenDeep hover:bg-white transition-colors">
                            <FileText className="w-4 h-4 shrink-0" /><span className="text-xs truncate max-w-[160px]">{m.attachment.filename}</span>
                          </a>
                        )
                      )}
                      {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-brand-soft flex items-center gap-2">
                <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Répondre…" className="flex-1 px-4 py-2.5 rounded-full bg-brand-softer outline-none text-sm" data-testid="contact-reply-input" />
                <button onClick={send} disabled={busy} className="w-11 h-11 rounded-full bg-brand-greenDeep text-white flex items-center justify-center disabled:opacity-60" data-testid="contact-reply-send">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-brand-green/45 text-sm">Sélectionnez un message</div>
          )}
        </div>
      </div>
    </div>
  );
}


function SecuritySettings() {
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState("demo");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/security-settings").then((r) => { setEnabled(r.data.two_factor_required !== false); setMode(r.data.two_factor_mode || "demo"); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const persist = async (nextEnabled, nextMode) => {
    setSaving(true);
    try { await api.put("/admin/security-settings", { two_factor_required: nextEnabled, two_factor_mode: nextMode }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); throw e; }
    finally { setSaving(false); }
  };
  const toggle = async (next) => {
    setEnabled(next);
    try { await persist(next, mode); toast.success(next ? "Sécurité 2FA activée à la connexion" : "Sécurité 2FA désactivée à la connexion"); }
    catch { setEnabled(!next); }
  };
  const changeMode = async (next) => {
    const prev = mode; setMode(next);
    try { await persist(enabled, next); toast.success(next === "production" ? "Mode PRODUCTION : le code est envoyé par email/SMS" : "Mode DÉMO : le code s'affiche à l'écran"); }
    catch { setMode(prev); }
  };
  return (
    <div data-testid="security-settings">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Sécurité à la connexion (2FA)</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-brand-greenDeep">Double authentification à la connexion</p>
            <p className="text-xs text-brand-green/50 mt-1 max-w-lg">Interrupteur global. Activé : les utilisateurs ayant activé le 2FA sur leur compte reçoivent un code à la connexion. Désactivé : aucun code 2FA n'est demandé, pour tout le monde.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={!loaded || saving}
            onClick={() => toggle(!enabled)}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-brand-limeDark" : "bg-brand-soft"}`}
            data-testid="twofa-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className={`text-xs mt-3 font-medium ${enabled ? "text-emerald-600" : "text-amber-600"}`} data-testid="twofa-status">
          {saving ? "Enregistrement…" : enabled ? "✓ Sécurité 2FA active" : "⚠ Sécurité 2FA désactivée à la connexion"}
        </p>

        <div className="mt-5 pt-5 border-t border-brand-soft">
          <p className="text-sm font-medium text-brand-greenDeep mb-1">Mode d'envoi du code</p>
          <p className="text-xs text-brand-green/50 mb-3 max-w-lg">« Démo » : le code s'affiche directement à l'écran (aucun envoi). « Production » : le code est envoyé par <b>email</b> ou <b>SMS</b> selon le choix de chaque utilisateur (email via SendGrid, SMS via Twilio).</p>
          <div className="inline-flex rounded-full bg-brand-softer p-1" data-testid="twofa-mode-switch">
            {[["demo", "Démo"], ["production", "Production"]].map(([v, l]) => (
              <button key={v} onClick={() => changeMode(v)} disabled={!loaded || saving}
                className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 ${mode === v ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`}
                data-testid={`twofa-mode-${v}`}>{l}</button>
            ))}
          </div>
          {mode === "production" && (
            <p className="text-[11px] text-amber-600 mt-2">Vérifiez que SendGrid (email) et/ou Twilio (SMS) sont configurés côté serveur, sinon le code ne partira pas.</p>
          )}
        </div>
      </div>
    </div>
  );
}


const PROFILE_LABELS = {
  bailleur: "Propriétaire Bailleur", locataire: "Locataire", agent: "Agent immobilier",
  gestionnaire: "Gestionnaire de biens", assureur: "Assureurs", artisan: "Artisan", courtier: "Courtier",
  referent: "Référent local",
};
const PROFILE_ORDER = ["bailleur", "locataire", "agent", "gestionnaire", "assureur", "artisan", "courtier", "referent"];

const MGMT_ROLES = ["bailleur", "agent", "gestionnaire", "courtier"];
const MGMT_TREE = [
  { key: "/app", label: "Tableau de bord", item: true },
  { key: "cat_actualites", label: "Actualités immobilières", children: [["/app/actualites", "Actualités"]] },
  { key: "cat_formation", label: "Formation & accompagnement", children: [["/app/formation", "Formation"], ["/app/coach", "Mon Coach"]] },
  { key: "cat_investissement", label: "Investissement immobilier", children: [["/app/offres-deals", "Offres & bons deals"], ["/app/simulateur", "Simulateur"]] },
  { key: "cat_gestion", label: "Gestion immobilière", children: [["/app/biens", "Biens"], ["/app/locataires", "Locataires"], ["/app/locations", "Locations"], ["/app/quittances", "Quittances"], ["/app/etats-des-lieux", "États des lieux"], ["/app/journal-envois", "Journal des envois"], ["/app/coffre", "Coffre-fort"], ["/app/messagerie", "Messagerie"], ["/app/assistant-ia", "Assistant IA"], ["/app/finances", "Finances"]] },
  { key: "cat_referent", label: "Référent local", children: [["/app/referents", "Annuaire des référents"], ["/app/referent-missions", "Mes missions"], ["/app/referent-releves", "Mes relevés"], ["/app/referent-profil", "Mon profil référent"], ["/app/etats-des-lieux#ref", "États des lieux"]] },
  { key: "cat_saisonnier", label: "Location saisonnière", children: [["/app/saisonnier", "Tableau saisonnier"], ["/app/saisonnier/calendrier", "Calendrier"], ["/app/saisonnier/reservations", "Réservations"], ["/app/saisonnier/reservation-directe", "Réservation directe"], ["/app/saisonnier/missions", "Missions & ménage"], ["/app/saisonnier/serrures", "Serrures connectées"], ["/app/saisonnier/tarifs", "Tarifs & messages"], ["/app/saisonnier/taxe-sejour", "Taxe de séjour"], ["/app/saisonnier/versements", "Versements plateformes"], ["/app/saisonnier/rapports", "Rapports"], ["/app/saisonnier/fiscalite", "Fiscalité meublé"], ["/app/saisonnier/livret", "Livret d'accueil"], ["/app/saisonnier/conformite", "Conformité"]] },
  { key: "cat_financement", label: "Financement & Assurance", children: [["/app/financement", "Offre de financement"], ["/app/assurances", "Assurance"]] },
  { key: "cat_travaux", label: "Travaux d'Entretien & Réparations", children: [["/app/artisans", "Artisans"], ["/app/travaux", "Travaux"], ["/app/articles", "Articles"]] },
];
const FLAT_KEYS = {
  locataire: [["/app/mon-espace", "Mon espace"], ["/app/assurances", "Assurances"], ["/app/mes-assurances", "Mes assurances"], ["/app/artisans", "Artisans"], ["/app/travaux", "Travaux"], ["/app/assistant-ia", "Assistant IA"]],
  assureur: [["/app/espace-assureur", "Espace assureur"], ["/app/assurances", "Assurances"]],
  artisan: [["/app/activite", "Mon activité"], ["/app/artisans", "Artisans"], ["/app/travaux", "Travaux"]],
  referent: [["/app/referent-dashboard", "Tableau de bord"], ["/app/referent-calendrier", "Calendrier"], ["/app/referent-messagerie", "Messagerie"], ["/app/referent", "Mes états des lieux"], ["/app/referent-releves", "Mes relevés"], ["/app/referent-profil", "Mon profil référent"]],
};

function MenuManager() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/menu-config").then((r) => { setConfig(r.data.config || {}); setDefaults(r.data.defaults || {}); }).catch(() => toast.error("Chargement impossible"));
  }, []);

  const has = (role, key) => (config[role] || []).includes(key);
  const setKeys = (role, next) => setConfig((c) => ({ ...c, [role]: Array.from(next) }));

  const toggleKey = (role, key) => {
    const cur = new Set(config[role] || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    setKeys(role, cur);
  };
  const toggleItemInCat = (role, catKey, itemKey) => {
    const cur = new Set(config[role] || []);
    if (cur.has(itemKey)) { cur.delete(itemKey); }
    else { cur.add(itemKey); cur.add(catKey); }
    setKeys(role, cur);
  };
  const toggleCat = (role, node) => {
    const cur = new Set(config[role] || []);
    const childKeys = node.children.map((c) => c[0]);
    const fullyOn = cur.has(node.key) && childKeys.every((k) => cur.has(k));
    if (fullyOn) { cur.delete(node.key); childKeys.forEach((k) => cur.delete(k)); }
    else { cur.add(node.key); childKeys.forEach((k) => cur.add(k)); }
    setKeys(role, cur);
  };
  const resetRole = (role) => setConfig((c) => ({ ...c, [role]: [...(defaults[role] || [])] }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/menu-config", { config }); toast.success("Menus enregistrés"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Accès administrateur requis"); }
    finally { setSaving(false); }
  };

  if (!config) return <div className="py-8 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="menu-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2"><Menu className="w-5 h-5" /> Menus par profil</h3>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="save-menu-config-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
      </div>
      <p className="text-sm text-brand-green/55 mb-4">Activez ou désactivez les catégories et sous-catégories visibles dans <code>/app</code> pour chaque profil. Décocher une catégorie masque tout le groupe. L'administrateur voit tout.</p>
      <div className="space-y-4">
        {PROFILE_ORDER.map((role) => {
          const isMgmt = MGMT_ROLES.includes(role);
          return (
            <div key={role} className="bg-white rounded-2xl border border-brand-soft p-5" data-testid={`profile-block-${role}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-brand-greenDeep">{PROFILE_LABELS[role]}</h4>
                <button onClick={() => resetRole(role)} className="text-xs text-brand-green/50 hover:text-brand-greenDeep font-medium" data-testid={`reset-${role}`}>Réinitialiser</button>
              </div>

              {isMgmt ? (
                <div className="space-y-3">
                  {MGMT_TREE.map((node) => {
                    if (node.item) {
                      const on = has(role, node.key);
                      return (
                        <label key={node.key} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm transition-colors ${on ? "border-brand-limeDark bg-brand-lime/10 text-brand-greenDeep" : "border-brand-soft text-brand-green/50"}`} data-testid={`menu-${role}-${node.key.replace(/\//g, "_")}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleKey(role, node.key)} className="w-4 h-4 accent-brand-limeDark" />
                          <span className="font-semibold">{node.label}</span>
                        </label>
                      );
                    }
                    const childKeys = node.children.map((c) => c[0]);
                    const catFullyOn = has(role, node.key) && childKeys.every((k) => has(role, k));
                    const catPartly = has(role, node.key) || childKeys.some((k) => has(role, k));
                    return (
                      <div key={node.key} className="rounded-xl border border-brand-soft overflow-hidden">
                        <label className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm transition-colors ${catPartly ? "bg-brand-softer text-brand-greenDeep" : "text-brand-green/50"}`} data-testid={`menu-cat-${role}-${node.key}`}>
                          <input type="checkbox" checked={catFullyOn} ref={(el) => { if (el) el.indeterminate = !catFullyOn && catPartly; }} onChange={() => toggleCat(role, node)} className="w-4 h-4 accent-brand-limeDark" />
                          <span className="font-semibold">{node.label}</span>
                        </label>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 border-t border-brand-soft bg-white">
                          {node.children.map(([ck, clabel]) => {
                            const on = has(role, ck);
                            return (
                              <label key={ck} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${on ? "border-brand-limeDark bg-brand-lime/10 text-brand-greenDeep" : "border-brand-soft text-brand-green/50"}`} data-testid={`menu-${role}-${ck.replace(/\//g, "_")}`}>
                                <input type="checkbox" checked={on} onChange={() => toggleItemInCat(role, node.key, ck)} className="w-4 h-4 accent-brand-limeDark" />
                                <span className="truncate">{clabel}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {(FLAT_KEYS[role] || []).map(([key, label]) => {
                    const on = has(role, key);
                    return (
                      <label key={key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm transition-colors ${on ? "border-brand-limeDark bg-brand-lime/10 text-brand-greenDeep" : "border-brand-soft text-brand-green/50"}`} data-testid={`menu-${role}-${key.replace(/\//g, "_")}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleKey(role, key)} className="w-4 h-4 accent-brand-limeDark" />
                        <span className="truncate">{label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CommissionManager() {
  const now = new Date();
  const [rate, setRate] = useState(null);
  const [savingRate, setSavingRate] = useState(false);
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [invoices, setInvoices] = useState(null);
  const [loadingInv, setLoadingInv] = useState(false);
  const [dl, setDl] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => { api.get("/insurance/commission-rate").then((r) => setRate(r.data.rate)).catch(() => setRate(10)); }, []);
  const loadInvoices = () => {
    setLoadingInv(true);
    api.get("/admin/commission-invoices", { params: { month } }).then((r) => setInvoices(r.data.invoices || [])).catch(() => toast.error("Chargement impossible")).finally(() => setLoadingInv(false));
  };
  useEffect(() => { loadInvoices(); /* eslint-disable-next-line */ }, [month]);

  const saveRate = async () => {
    setSavingRate(true);
    try { await api.put("/admin/commission-rate", { rate: Number(rate) }); toast.success("Taux enregistré"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingRate(false); }
  };
  const genInvoices = async () => {
    setGenBusy(true);
    try {
      const res = await api.post("/admin/commission-invoices/generate", null, { params: { month } });
      toast.success(res.data.created > 0 ? `${res.data.created} facture(s) émise(s)` : "Factures déjà émises pour ce mois");
      loadInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setGenBusy(false); }
  };
  const toggleStatus = async (inv) => {
    const next = inv.status === "payée" ? "en attente" : "payée";
    try {
      await api.put(`/admin/commission-invoices/${inv.invoice_id}/status`, { status: next });
      setInvoices((iv) => iv.map((x) => (x.invoice_id === inv.invoice_id ? { ...x, status: next } : x)));
    } catch { toast.error("Erreur"); }
  };
  const downloadInvoice = async (iid) => {
    setDl(iid);
    try {
      const res = await api.get(`/admin/commission-invoice/${iid}`, { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `commissions-${iid}-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Facture indisponible"); }
    finally { setDl(""); }
  };

  return (
    <div data-testid="commission-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2 mb-1"><Wallet className="w-5 h-5" /> Commissions d'assurance</h3>
      <p className="text-sm text-brand-green/55 mb-4">Définissez le taux prélevé sur chaque souscription, puis générez la facture mensuelle par assureur.</p>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 mb-4 flex flex-wrap items-end gap-3">
        <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Taux de commission (%)</span>
          <input type="number" step="0.5" min="0" max="100" value={rate ?? ""} onChange={(e) => setRate(e.target.value)} className="admin-inp w-40" data-testid="commission-rate-input" />
        </label>
        <button onClick={saveRate} disabled={savingRate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="save-commission-rate-btn">
          {savingRate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h4 className="font-semibold text-brand-greenDeep">Factures du mois</h4>
          <div className="flex items-center gap-2">
            <button onClick={genInvoices} disabled={genBusy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-emerald text-white font-semibold text-sm disabled:opacity-60" data-testid="generate-invoices-btn">
              {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Émettre les factures
            </button>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="admin-inp w-44" data-testid="commission-month" />
          </div>
        </div>
        {loadingInv ? <div className="py-6 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          (invoices && invoices.length) ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-softer text-left text-brand-green/60"><tr>
                  <th className="px-4 py-2.5 font-medium">Assureur</th>
                  <th className="px-4 py-2.5 font-medium text-right">Souscriptions</th>
                  <th className="px-4 py-2.5 font-medium text-right">Total encaissé</th>
                  <th className="px-4 py-2.5 font-medium text-right">Commission due</th>
                  <th className="px-4 py-2.5 font-medium text-center">Statut</th>
                  <th className="px-4 py-2.5 font-medium text-right">Facture</th>
                </tr></thead>
                <tbody className="divide-y divide-brand-soft">
                  {invoices.map((inv) => (
                    <tr key={inv.insurer_id} data-testid={`commission-row-${inv.insurer_id}`}>
                      <td className="px-4 py-2.5 font-medium text-brand-greenDeep">{inv.insurer_name}</td>
                      <td className="px-4 py-2.5 text-right">{inv.count}</td>
                      <td className="px-4 py-2.5 text-right">{inv.subscriptions_total.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-brand-greenDeep">{inv.commission_total.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-2.5 text-center">
                        {inv.invoice_id ? (
                          <button onClick={() => toggleStatus(inv)} className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${inv.status === "payée" ? "bg-brand-emerald/10 text-brand-emerald" : "bg-amber-100 text-amber-700"}`} data-testid={`invoice-status-${inv.insurer_id}`}>{inv.status === "payée" ? "Payée" : "En attente"}</button>
                        ) : <span className="text-[11px] text-brand-green/40">non émise</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => downloadInvoice(inv.insurer_id)} disabled={dl === inv.insurer_id} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer font-semibold disabled:opacity-60" data-testid={`invoice-pdf-${inv.insurer_id}`}>
                          {dl === inv.insurer_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="py-6 text-center text-sm text-brand-green/50">Aucune souscription active ce mois-ci.</p>
        )}
      </div>
      <style>{`.admin-inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;font-size:0.85rem}.admin-inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
    </div>
  );
}

function OfferManager() {
  const [offers, setOffers] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/offers").then((r) => setOffers(r.data.offers || [])).catch(() => toast.error("Chargement impossible")); }, []);

  const upd = (i, key, val) => setOffers((os) => os.map((o, idx) => (idx === i ? { ...o, [key]: val } : o)));
  const save = async () => {
    setSaving(true);
    try {
      const payload = offers.map((o) => ({ ...o, price_monthly: Number(o.price_monthly) || 0, price_yearly: Number(o.price_yearly) || 0, max_properties: Number(o.max_properties) || 0, str_included: o.str_included ?? ["premium", "pro"].includes(o.id), promo_active: !!o.promo_active, promo_price_monthly: Number(o.promo_price_monthly) || 0, promo_price_yearly: Number(o.promo_price_yearly) || 0, promo_label: (o.promo_label || "").trim(), promo_end: (o.promo_end || "").trim(), services: typeof o.services === "string" ? o.services.split("\n").map((s) => s.trim()).filter(Boolean) : o.services }));
      await api.put("/admin/offers", { offers: payload });
      toast.success("Offres enregistrées");
    } catch (e) { toast.error(e?.response?.data?.detail || "Accès administrateur requis"); }
    finally { setSaving(false); }
  };

  if (!offers) return <div className="py-8 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="offer-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2"><Wallet className="w-5 h-5" /> Offres commerciales</h3>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="save-offers-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
      </div>
      <p className="text-sm text-brand-green/55 mb-4">Configurez le nom, les prix (mensuel & annuel), la limite de biens et les services de chaque offre. Ces tarifs alimentent la page publique.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {offers.map((o, i) => (
          <div key={o.id || i} className="bg-white rounded-2xl border border-brand-soft p-4 space-y-2" data-testid={`offer-edit-${i}`}>
            <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Nom</span><input value={o.name || ""} onChange={(e) => upd(i, "name", e.target.value)} className="admin-inp" data-testid={`offer-name-${i}`} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Prix / mois (€)</span><input type="number" step="0.01" value={o.price_monthly ?? 0} onChange={(e) => upd(i, "price_monthly", e.target.value)} className="admin-inp" data-testid={`offer-monthly-${i}`} /></label>
              <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Prix / an (€)</span><input type="number" step="0.01" value={o.price_yearly ?? 0} onChange={(e) => upd(i, "price_yearly", e.target.value)} className="admin-inp" data-testid={`offer-yearly-${i}`} /></label>
            </div>
            <label className="flex items-center gap-2 text-xs text-brand-greenDeep cursor-pointer select-none"><input type="checkbox" checked={o.str_included ?? ["premium", "pro"].includes(o.id)} onChange={(e) => upd(i, "str_included", e.target.checked)} data-testid={`offer-str-${i}`} /> Location saisonnière incluse</label>
            <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Limite de biens (0 = illimité)</span><input type="number" value={o.max_properties ?? 0} onChange={(e) => upd(i, "max_properties", e.target.value)} className="admin-inp" data-testid={`offer-max-${i}`} /></label>
            <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Services (1 par ligne)</span><textarea rows={5} value={Array.isArray(o.services) ? o.services.join("\n") : o.services || ""} onChange={(e) => upd(i, "services", e.target.value)} className="admin-inp resize-none" data-testid={`offer-services-${i}`} /></label>
            <div className="rounded-xl bg-brand-gold/5 border border-brand-gold/30 p-2.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={!!o.promo_active} onChange={(e) => upd(i, "promo_active", e.target.checked)} data-testid={`offer-promo-active-${i}`} />
                <span className="text-[12px] font-semibold text-brand-limeDark">🎁 Promotion active</span>
              </label>
              {o.promo_active && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Promo / mois (€)</span><input type="number" step="0.01" value={o.promo_price_monthly ?? 0} onChange={(e) => upd(i, "promo_price_monthly", e.target.value)} className="admin-inp" data-testid={`offer-promo-monthly-${i}`} /></label>
                    <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Promo / an (€)</span><input type="number" step="0.01" value={o.promo_price_yearly ?? 0} onChange={(e) => upd(i, "promo_price_yearly", e.target.value)} className="admin-inp" data-testid={`offer-promo-yearly-${i}`} /></label>
                  </div>
                  <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Libellé promo (optionnel)</span><input value={o.promo_label || ""} onChange={(e) => upd(i, "promo_label", e.target.value)} placeholder="Ex. Offre de lancement" className="admin-inp" data-testid={`offer-promo-label-${i}`} /></label>
                  <label className="block"><span className="block text-[11px] text-brand-green/60 mb-1">Fin de la promo (optionnel)</span><input type="date" value={o.promo_end || ""} onChange={(e) => upd(i, "promo_end", e.target.value)} className="admin-inp" data-testid={`offer-promo-end-${i}`} /></label>
                  <p className="text-[10px] text-brand-green/45">0 € = offre gratuite pendant la promo. Le prix initial sera affiché barré. Une date de fin affiche un compte à rebours sur la page Tarifs.</p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <style>{`.admin-inp{width:100%;padding:0.45rem 0.7rem;border-radius:0.6rem;border:1px solid #efe7d6;outline:none;font-size:0.8rem}.admin-inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
    </div>
  );
}

function AccessManager() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");
  const [planOpts, setPlanOpts] = useState(["Gratuit", "Premium", "Pro"]);

  const load = () => api.get("/auth/admin/users").then((r) => {
    const seen = new Set();
    const uniq = (r.data || []).filter((u) => (u.user_id && !seen.has(u.user_id)) ? seen.add(u.user_id) : false);
    setUsers(uniq);
  }).catch(() => toast.error("Chargement impossible"));
  useEffect(() => {
    load();
    api.get("/offers").then((r) => { const n = (r.data.offers || []).map((o) => o.name).filter(Boolean); if (n.length) setPlanOpts(n); }).catch(() => {});
  }, []);

  const setAccess = async (uid, status) => {
    setBusy(uid);
    try {
      await api.put(`/auth/admin/users/${uid}/access`, { status });
      toast.success(status === "approved" ? "Accès autorisé" : status === "revoked" ? "Accès révoqué" : "Remis en attente");
      setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, access_status: status } : u)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const changePlan = async (uid, plan) => {
    setBusy(uid);
    try {
      await api.put(`/auth/admin/users/${uid}/plan`, { plan });
      toast.success(`Formule mise à jour : ${plan}`);
      setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, plan } : u)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const changeRole = async (uid, role) => {
    setBusy(uid);
    try {
      await api.put(`/auth/admin/users/${uid}/role`, { role });
      toast.success("Profil mis à jour");
      setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, role, access_status: "approved" } : u)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const ROLE_OPTS = [["bailleur", "Bailleur"], ["locataire", "Locataire"], ["agent", "Agent"], ["gestionnaire", "Gestionnaire"], ["assureur", "Assureur"], ["artisan", "Artisan"], ["courtier", "Courtier"], ["referent", "Référent"], ["admin", "Admin"]];

  if (!users) return <div className="py-8 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const pending = users.filter((u) => u.access_status === "pending");
  const badge = { approved: "bg-brand-emerald/10 text-brand-emerald", pending: "bg-amber-100 text-amber-700", revoked: "bg-red-100 text-red-700" };
  const badgeLabel = { approved: "Autorisé", pending: "En attente", revoked: "Révoqué" };

  const Row = (u) => (
    <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-soft p-3" data-testid={`user-row-${u.user_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-brand-greenDeep truncate">{u.name || u.email} <span className="text-xs text-brand-green/40">· {u.role || "—"}</span></p>
        <p className="text-xs text-brand-green/50 truncate">{u.email}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={u.role || ""} onChange={(e) => changeRole(u.user_id, e.target.value)} disabled={busy === u.user_id}
          className="text-xs px-2.5 py-1.5 rounded-full border border-brand-soft bg-white text-brand-greenDeep outline-none focus:border-brand-lime disabled:opacity-60" data-testid={`role-select-${u.user_id}`}>
          <option value="" disabled>Profil…</option>
          {ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={u.plan || "Gratuit"} onChange={(e) => changePlan(u.user_id, e.target.value)} disabled={busy === u.user_id}
          className="text-xs px-2.5 py-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/5 text-brand-limeDark font-semibold outline-none focus:border-brand-lime disabled:opacity-60" title="Formule d'abonnement" data-testid={`plan-select-${u.user_id}`}>
          {planOpts.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${badge[u.access_status] || badge.approved}`} data-testid={`access-badge-${u.user_id}`}>{badgeLabel[u.access_status] || "Autorisé"}</span>
        {u.access_status !== "approved" && <button onClick={() => setAccess(u.user_id, "approved")} disabled={busy === u.user_id} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand-emerald hover:opacity-90 text-white font-semibold disabled:opacity-60" data-testid={`approve-${u.user_id}`}>{busy === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Autoriser</button>}
        {u.access_status !== "revoked" && u.role !== "admin" && <button onClick={() => setAccess(u.user_id, "revoked")} disabled={busy === u.user_id} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 font-semibold disabled:opacity-60" data-testid={`revoke-${u.user_id}`}><X className="w-3.5 h-3.5" /> Révoquer</button>}
      </div>
    </div>
  );

  return (
    <div data-testid="access-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2 mb-1"><Users className="w-5 h-5" /> Gestion des accès & profils</h3>
      <p className="text-sm text-brand-green/55 mb-4">Autorisez/révoquez l'accès et modifiez le profil d'un compte (seul l'admin peut changer un profil après le choix initial de l'utilisateur).</p>
      {pending.length > 0 && (
        <div className="mb-5">
          <h4 className="font-semibold text-brand-greenDeep mb-2">À valider ({pending.length})</h4>
          <div className="space-y-2">{pending.map(Row)}</div>
        </div>
      )}
      <h4 className="font-semibold text-brand-greenDeep mb-2">Tous les comptes ({users.length})</h4>
      <div className="space-y-2">{users.map(Row)}</div>
    </div>
  );
}

function SignupProfilesManager() {
  const [profiles, setProfiles] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/auth/admin/signup-profiles").then((r) => setProfiles(r.data || [])).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);
  const toggle = (role) => setProfiles((ps) => ps.map((p) => (p.role === role ? { ...p, active: !p.active } : p)));
  const save = async () => {
    setSaving(true);
    try {
      const roles = {}; (profiles || []).forEach((p) => { roles[p.role] = p.active; });
      await api.put("/auth/admin/signup-profiles", { roles });
      toast.success("Profils d'inscription mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  if (!profiles) return null;
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-6" data-testid="signup-profiles-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5" /> Profils proposés à l'inscription</h3>
      <p className="text-sm text-brand-green/55 mb-4">Activez/désactivez les profils que les nouveaux utilisateurs peuvent choisir. Le profil « Administrateur » n'est jamais proposé.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        {profiles.map((p) => (
          <label key={p.role} className="flex items-center justify-between gap-3 rounded-xl border border-brand-soft p-3 cursor-pointer" data-testid={`signup-profile-${p.role}`}>
            <span className="text-sm font-medium text-brand-greenDeep">{p.label}</span>
            <input type="checkbox" checked={p.active} onChange={() => toggle(p.role)} className="w-5 h-5 accent-brand-lime" data-testid={`signup-profile-toggle-${p.role}`} />
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="signup-profiles-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
      </button>
    </div>
  );
}

function ReferentAdminManager() {
  const [refs, setRefs] = useState([]);
  const [rate, setRate] = useState(10);
  const [savingRate, setSavingRate] = useState(false);
  const load = () => api.get("/referents").then((r) => setRefs(r.data.referents || [])).catch(() => {});
  useEffect(() => { load(); api.get("/referents/commission-rate").then((r) => setRate(r.data.rate)).catch(() => {}); }, []);
  const setStatus = async (id, status) => { try { await api.put(`/admin/referents/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); } catch { toast.error("Erreur"); } };
  const saveRate = async () => { setSavingRate(true); try { await api.put("/admin/referent-commission-rate", { rate: Number(rate) }); toast.success("Commission enregistrée"); } catch { toast.error("Erreur"); } finally { setSavingRate(false); } };
  const pending = refs.filter((r) => r.status !== "approved");
  return (
    <div data-testid="referent-admin-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Référents locaux — validation & commission</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-4">
        <div className="flex items-end gap-3">
          <label className="block"><span className="text-xs text-brand-green/60">Commission plateforme (%)</span>
            <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-32 px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1" data-testid="referent-rate-input" /></label>
          <button onClick={saveRate} disabled={savingRate} className="px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="referent-rate-save">{savingRate ? "…" : "Enregistrer"}</button>
        </div>
        <div>
          <p className="text-sm font-semibold text-brand-greenDeep mb-2">Référents ({refs.length})</p>
          {pending.length > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="referent-pending-note">
              {pending.length} candidature(s) en attente de validation. Les candidats reçoivent une relance automatique par e-mail tant que leur profil n'est pas traité.
            </p>
          )}
          <div className="space-y-2">
            {refs.length === 0 && <p className="text-sm text-brand-green/45">Aucun référent.</p>}
            {refs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-brand-softer" data-testid={`referent-admin-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-greenDeep truncate">{r.name} <span className="text-xs text-brand-green/50">· {r.city || "—"} · {r.email || ""}</span></p>
                  <p className="text-xs text-brand-green/50">{(r.missions || []).length} mission(s){r.contract_signed ? " · contrat signé" : ""}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                  {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} className="px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid={`referent-approve-${r.id}`}>Valider</button>}
                  {r.status === "approved" && <button onClick={() => setStatus(r.id, "rejected")} className="px-3 py-1 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold" data-testid={`referent-revoke-${r.id}`}>Révoquer</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function ReferentPricingManager() {
  const [grid, setGrid] = useState(null);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nt, setNt] = useState({ label: "", client: "", referent: "" });
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/referents/pricing").then((r) => { setGrid(r.data.pricing || {}); setTypes(r.data.mission_types || []); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const setVal = (k, field, v) => setGrid((g) => ({ ...g, [k]: { ...g[k], [field]: v } }));
  const save = async () => {
    setBusy(true);
    const prices = {};
    Object.entries(grid).forEach(([k, v]) => { prices[k] = { client: Number(v.client) || 0, referent: Number(v.referent) || 0 }; });
    try { await api.put("/admin/referent-pricing", { prices }); toast.success("Tarification enregistrée"); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  const addType = async () => {
    if (!nt.label.trim()) { toast.error("Indiquez un libellé"); return; }
    setAdding(true);
    try { await api.post("/admin/referent-mission-types", { label: nt.label.trim(), client: Number(nt.client) || 0, referent: Number(nt.referent) || 0 }); toast.success("Type de mission ajouté"); setNt({ label: "", client: "", referent: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setAdding(false); }
  };
  const removeType = async (t) => {
    if (!window.confirm(`Supprimer le type de mission « ${t.label} » ?`)) return;
    try { await api.delete(`/admin/referent-mission-types/${t.key}`); toast.success("Type supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const toggleActive = async (t) => {
    setTypes((arr) => arr.map((x) => (x.key === t.key ? { ...x, active: !t.active } : x)));
    try { await api.put(`/admin/referent-mission-types/${t.key}/active`, { active: !t.active }); toast.success(!t.active ? "Type activé" : "Type désactivé"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); load(); }
  };
  const [dragIdx, setDragIdx] = useState(null);
  const onDrop = async (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const arr = [...types];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    setTypes(arr); setDragIdx(null);
    try { await api.put("/admin/referent-mission-types/reorder", { order: arr.map((t) => t.key) }); toast.success("Ordre mis à jour"); }
    catch { toast.error("Erreur"); load(); }
  };
  if (!grid) return null;
  return (
    <div data-testid="referent-pricing-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Tarification des missions référent</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-3">
        <p className="text-xs text-brand-green/55">Pour chaque type de mission : le <b>prix facturé au propriétaire</b> et le <b>montant reversé au référent</b>. Glissez-déposez les lignes pour changer l'<b>ordre d'affichage</b>, utilisez l'interrupteur pour <b>activer/désactiver</b> un type, ou ajoutez vos propres types ci-dessous.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-brand-green/60"><tr><th className="py-2"></th><th className="text-left py-2 font-semibold">Mission</th><th className="text-left py-2 font-semibold">Prix propriétaire (€)</th><th className="text-left py-2 font-semibold">Reversé référent (€)</th><th className="text-left py-2 font-semibold">Marge site</th><th className="text-left py-2 font-semibold">Actif</th><th className="py-2"></th></tr></thead>
            <tbody>{types.map((t, i) => { const v = grid[t.key] || { client: 0, referent: 0 }; const margin = (Number(v.client) || 0) - (Number(v.referent) || 0); const inactive = t.active === false; return (
              <tr key={t.key} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} className={`border-t border-brand-soft ${dragIdx === i ? "opacity-40" : ""} ${inactive ? "bg-brand-softer/60" : ""}`} data-testid={`pricing-row-${t.key}`}>
                <td className="py-2 pr-1 cursor-grab active:cursor-grabbing text-brand-green/40" data-testid={`pricing-drag-${t.key}`}><GripVertical className="w-4 h-4" /></td>
                <td className={`py-2 pr-3 ${inactive ? "text-brand-green/40" : "text-brand-greenDeep"}`}>{t.label}{t.custom && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep">Personnalisé</span>}{inactive && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-soft text-brand-green/60">Inactif</span>}</td>
                <td className="py-2 pr-3"><input type="number" value={v.client} onChange={(e) => setVal(t.key, "client", e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid={`pricing-client-${t.key}`} /></td>
                <td className="py-2 pr-3"><input type="number" value={v.referent} onChange={(e) => setVal(t.key, "referent", e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid={`pricing-referent-${t.key}`} /></td>
                <td className="py-2 text-brand-green/70 font-medium">{margin.toLocaleString("fr-FR")} €</td>
                <td className="py-2"><button onClick={() => toggleActive(t)} role="switch" aria-checked={!inactive} className={`relative w-11 h-6 rounded-full transition-colors ${inactive ? "bg-brand-soft" : "bg-brand-greenDeep"}`} data-testid={`pricing-active-${t.key}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${inactive ? "left-0.5" : "left-[22px]"}`} /></button></td>
                <td className="py-2 text-right">{t.custom && <button onClick={() => removeType(t)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500" title="Supprimer ce type" data-testid={`pricing-delete-${t.key}`}><X className="w-4 h-4" /></button>}</td>
              </tr>); })}</tbody>
          </table>
        </div>
        <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="pricing-save-btn">{busy ? "…" : "Enregistrer la tarification"}</button>
        <div className="border-t border-brand-soft pt-4 mt-2" data-testid="pricing-add-type">
          <p className="text-sm font-semibold text-brand-greenDeep mb-2 flex items-center gap-1.5"><Check className="w-4 h-4" /> Ajouter un type de mission</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Libellé</span><input value={nt.label} onChange={(e) => setNt({ ...nt, label: e.target.value })} placeholder="Ex : Diagnostic avant travaux" className="w-64 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid="new-type-label" /></label>
            <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Prix propriétaire (€)</span><input type="number" value={nt.client} onChange={(e) => setNt({ ...nt, client: e.target.value })} className="w-32 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid="new-type-client" /></label>
            <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Reversé référent (€)</span><input type="number" value={nt.referent} onChange={(e) => setNt({ ...nt, referent: e.target.value })} className="w-32 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid="new-type-referent" /></label>
            <button onClick={addType} disabled={adding} className="px-5 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="new-type-add-btn">{adding ? "…" : "Ajouter"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ReferentPaymentPolicyManager() {
  const [pol, setPol] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/referents/payment-policy").then((r) => setPol(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/referent-payment-policy", {
        reminder_hours: Number(pol.reminder_hours) || 0,
        cancel_days: Number(pol.cancel_days) || 0,
        auto_reminder_enabled: !!pol.auto_reminder_enabled,
        auto_cancel_enabled: !!pol.auto_cancel_enabled,
      });
      toast.success("Politique de paiement enregistrée");
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  if (!pol) return null;
  return (
    <div data-testid="referent-payment-policy-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Missions en attente de paiement — relance & annulation auto</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-4">
        <p className="text-xs text-brand-green/55">Après acceptation par le référent, la mission passe « En attente paiement ». Ces réglages automatisent la relance du propriétaire et l'annulation si le règlement n'arrive pas.</p>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-brand-greenDeep">Relance automatique du propriétaire</p>
            <p className="text-xs text-brand-green/55">E-mail de relance (avec lien de paiement) répété à cet intervalle, sans action du référent.</p>
          </div>
          <label className="inline-flex items-center gap-2 shrink-0"><input type="checkbox" checked={!!pol.auto_reminder_enabled} onChange={(e) => setPol({ ...pol, auto_reminder_enabled: e.target.checked })} data-testid="policy-reminder-toggle" /><span className="text-sm text-brand-green/70">Activée</span></label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-brand-green/70">Relancer toutes les</span>
          <input type="number" min="0" value={pol.reminder_hours} onChange={(e) => setPol({ ...pol, reminder_hours: e.target.value })} className="w-20 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid="policy-reminder-hours" />
          <span className="text-sm text-brand-green/70">heures</span>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-brand-soft pt-4">
          <div>
            <p className="text-sm font-semibold text-brand-greenDeep">Annulation automatique</p>
            <p className="text-xs text-brand-green/55">La mission est annulée si le propriétaire n'a pas payé dans ce délai.</p>
          </div>
          <label className="inline-flex items-center gap-2 shrink-0"><input type="checkbox" checked={!!pol.auto_cancel_enabled} onChange={(e) => setPol({ ...pol, auto_cancel_enabled: e.target.checked })} data-testid="policy-cancel-toggle" /><span className="text-sm text-brand-green/70">Activée</span></label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-brand-green/70">Annuler après</span>
          <input type="number" min="0" value={pol.cancel_days} onChange={(e) => setPol({ ...pol, cancel_days: e.target.value })} className="w-20 px-2 py-1.5 rounded-lg border border-brand-soft" data-testid="policy-cancel-days" />
          <span className="text-sm text-brand-green/70">jours</span>
        </div>
        <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="policy-save-btn">{busy ? "…" : "Enregistrer la politique"}</button>
      </div>
    </div>
  );
}


function ReferentTestPaymentManager() {
  const [rows, setRows] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/referent-awaiting-payment").then((r) => { setRows(r.data.missions || []); setTestMode(!!r.data.test_mode); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const simulate = async (id) => {
    setBusy(id);
    try { await api.post(`/referents/missions/${id}/test-mark-paid`); toast.success("Paiement simulé — mission passée à « Payé à exécuter »"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };
  if (!testMode) return null;
  return (
    <div data-testid="referent-test-payment-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Wallet className="w-5 h-5" /> Simulation de paiement (environnement de test)</h3>
      <div className="bg-white rounded-2xl border border-dashed border-purple-300 p-5">
        <p className="text-xs text-brand-green/55 mb-4">Outil technique réservé à l'environnement de test : force une mission « En attente paiement » → « Payé à exécuter » sans passer par Stripe. Le référent est crédité (commission/net) comme un vrai paiement.</p>
        {rows.length === 0 ? (
          <p className="text-sm text-brand-green/45 py-4" data-testid="test-payment-empty">Aucune mission en attente de paiement.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-brand-green/60"><tr><th className="text-left py-2 font-semibold">Mission / Bien</th><th className="text-left py-2 font-semibold">Propriétaire</th><th className="text-left py-2 font-semibold">Référent</th><th className="text-left py-2 font-semibold">Montant</th><th className="py-2"></th></tr></thead>
              <tbody>{rows.map((m) => (
                <tr key={m.id} className="border-t border-brand-soft" data-testid={`test-payment-row-${m.id}`}>
                  <td className="py-2.5 pr-3 text-brand-greenDeep">{m.mission_label}<span className="block text-xs text-brand-green/50">{m.property_name || "—"}</span></td>
                  <td className="py-2.5 pr-3 text-brand-green/70">{m.owner_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-brand-green/70">{m.referent_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-brand-greenDeep font-semibold">{Number(m.amount).toLocaleString("fr-FR")} €</td>
                  <td className="py-2.5 text-right"><button onClick={() => simulate(m.id)} disabled={busy === m.id} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-purple-600 text-white text-xs font-semibold disabled:opacity-60" data-testid={`test-pay-btn-${m.id}`}>{busy === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />} Simuler le paiement</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function ReferentRefundsManager() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/referent-refunds-pending").then((r) => setRows(r.data.refunds || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const retry = async (id) => {
    setBusy(id + ":retry");
    try { const r = await api.post(`/admin/referent-missions/${id}/retry-refund`); toast.success("Remboursement Stripe effectué"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec du remboursement Stripe"); } finally { setBusy(""); }
  };
  const markManual = async (id) => {
    if (!window.confirm("Confirmer que ce remboursement a été effectué manuellement (hors Stripe) ?")) return;
    setBusy(id + ":manual");
    try { await api.post(`/admin/referent-missions/${id}/mark-refunded`); toast.success("Marqué comme remboursé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  };
  return (
    <div data-testid="referent-refunds-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Wallet className="w-5 h-5" /> Remboursements en attente</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <p className="text-xs text-brand-green/55 mb-4">Missions annulées dont le remboursement Stripe automatique n'a pas abouti. Retentez le remboursement Stripe ou marquez-le remboursé manuellement (virement, etc.).</p>
        {rows.length === 0 ? (
          <p className="text-sm text-brand-green/45 py-4" data-testid="refunds-empty">Aucun remboursement en attente. 🎉</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-brand-green/60"><tr>
                <th className="text-left py-2 font-semibold">Mission / Bien</th><th className="text-left py-2 font-semibold">Propriétaire</th><th className="text-left py-2 font-semibold">Montant</th><th className="text-left py-2 font-semibold">Motif</th><th className="text-left py-2 font-semibold">Annulé par</th><th className="py-2"></th>
              </tr></thead>
              <tbody>{rows.map((m) => (
                <tr key={m.id} className="border-t border-brand-soft align-top" data-testid={`refund-row-${m.id}`}>
                  <td className="py-2.5 pr-3 text-brand-greenDeep">{m.mission_label}<span className="block text-xs text-brand-green/50">{m.property_name || "—"}</span></td>
                  <td className="py-2.5 pr-3 text-brand-green/70">{m.owner_name || "—"}<span className="block text-xs text-brand-green/45">{m.owner_email || ""}</span></td>
                  <td className="py-2.5 pr-3 text-brand-greenDeep font-semibold">{Number(m.amount).toLocaleString("fr-FR")} €</td>
                  <td className="py-2.5 pr-3 text-brand-green/70 max-w-[220px]">{m.cancel_note ? <span className="italic">« {m.cancel_note} »</span> : <span className="text-brand-green/35">—</span>}</td>
                  <td className="py-2.5 pr-3 text-brand-green/60 text-xs">{m.cancelled_by || "—"}</td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex gap-2 flex-wrap justify-end">
                      {m.has_session && <button onClick={() => retry(m.id)} disabled={busy === m.id + ":retry"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep text-white text-xs font-semibold disabled:opacity-60" data-testid={`refund-retry-${m.id}`}>{busy === m.id + ":retry" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />} Retenter Stripe</button>}
                      <button onClick={() => markManual(m.id)} disabled={busy === m.id + ":manual"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft text-brand-greenDeep text-xs font-semibold disabled:opacity-60" data-testid={`refund-manual-${m.id}`}>{busy === m.id + ":manual" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Marquer remboursé</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



function ReferentStatementsManager() {
  const [refs, setRefs] = useState([]);
  const [statements, setStatements] = useState([]);
  const [refId, setRefId] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/referents/statements").then((r) => setStatements(r.data.statements || [])).catch(() => {});
  useEffect(() => { api.get("/referents").then((r) => setRefs(r.data.referents || [])).catch(() => {}); load(); }, []);
  const generate = async () => {
    if (!refId) { toast.error("Sélectionnez un référent"); return; }
    setBusy(true);
    try { await api.post("/referents/statements/generate", { referent_id: refId, period }); toast.success("Relevé généré"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const markPaid = async (id) => { try { await api.post(`/admin/referents/statements/${id}/pay`); toast.success("Virement marqué comme effectué"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };
  const badge = { draft: ["À valider par le référent", "bg-amber-100 text-amber-700"], validated: ["Validé — à virer", "bg-blue-100 text-blue-700"], paid: ["Payé", "bg-emerald-100 text-emerald-700"] };
  return (
    <div data-testid="referent-statements-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Relevés & factures des référents</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block"><span className="text-xs text-brand-green/60">Référent</span>
            <select value={refId} onChange={(e) => setRefId(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1 min-w-[180px]" data-testid="stmt-referent-select"><option value="">— Choisir —</option>{refs.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
          <label className="block"><span className="text-xs text-brand-green/60">Période (mois)</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1" data-testid="stmt-period" /></label>
          <button onClick={generate} disabled={busy} className="px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="stmt-generate-btn">{busy ? "…" : "Générer le relevé"}</button>
        </div>
        <div className="space-y-2">
          {statements.length === 0 && <p className="text-sm text-brand-green/45">Aucun relevé.</p>}
          {statements.map((s) => { const [bl, bc] = badge[s.status] || [s.status, "bg-brand-soft"]; return (
            <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-brand-softer flex-wrap" data-testid={`admin-stmt-${s.id}`}>
              <div><p className="text-sm font-medium text-brand-greenDeep">{s.referent_name} · {s.period} · {s.invoice_number}</p>
                <p className="text-xs text-brand-green/50">{s.mission_count} mission(s) · {Number(s.total_amount).toLocaleString("fr-FR")} €</p></div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${bc}`}>{bl}</span>
                {s.status === "validated" && <button onClick={() => markPaid(s.id)} className="px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid={`stmt-pay-${s.id}`}>Marquer viré</button>}
              </div>
            </div>
          ); })}
        </div>
      </div>
    </div>
  );
}


function EdlModuleManager() {
  const [cfg, setCfg] = useState(null);
  const [offer, setOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  useEffect(() => {
    api.get("/edl/module-config").then((r) => setCfg({ enabled: r.data.enabled, mode: r.data.mode })).catch(() => {});
    api.get("/edl/offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);
  const saveModule = async (next) => {
    setCfg(next); setSaving(true);
    try { await api.put("/admin/edl-module", next); toast.success("Module États des lieux mis à jour"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const setSvc = (i, v) => setOffer((c) => ({ ...c, services: c.services.map((s, idx) => (idx === i ? v : s)) }));
  const addSvc = () => setOffer((c) => ({ ...c, services: [...(c.services || []), ""] }));
  const removeSvc = (i) => setOffer((c) => ({ ...c, services: (c.services || []).filter((_, idx) => idx !== i) }));
  const saveOffer = async () => {
    setSavingOffer(true);
    try {
      await api.put("/admin/edl-offer", {
        name: offer.name, price_monthly: Number(offer.price_monthly) || 0,
        price_yearly: Number(offer.price_yearly) || 0, enabled: !!offer.enabled,
        services: (offer.services || []).map((s) => s).filter(Boolean),
      });
      toast.success("Offre États des lieux enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingOffer(false); }
  };
  const MODES = [["integrated", "Intégré (listes déroulantes du site)"], ["independent", "Indépendant (saisie manuelle)"], ["both", "Les deux (au choix)"]];
  return (
    <div data-testid="edl-module-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Module États des lieux — activation & offre</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-5">
        {cfg && (
          <>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-brand-greenDeep">Activer le module États des lieux</p>
                <p className="text-xs text-brand-green/50 mt-1 max-w-lg">Désactivé : le module est masqué pour tous les utilisateurs (sauf administrateur).</p>
              </div>
              <button type="button" role="switch" aria-checked={cfg.enabled} disabled={saving}
                onClick={() => saveModule({ ...cfg, enabled: !cfg.enabled })}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${cfg.enabled ? "bg-brand-limeDark" : "bg-brand-soft"}`}
                data-testid="edl-module-toggle">
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${cfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <div>
              <p className="text-sm font-medium text-brand-greenDeep mb-2">Mode de saisie</p>
              <div className="flex flex-wrap gap-2">
                {MODES.map(([val, lbl]) => (
                  <button key={val} type="button" disabled={saving || !cfg.enabled}
                    onClick={() => saveModule({ ...cfg, mode: val })}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors disabled:opacity-50 ${cfg.mode === val ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "bg-white text-brand-greenDeep border-brand-soft hover:bg-brand-softer"}`}
                    data-testid={`edl-mode-${val}`}>{lbl}</button>
                ))}
              </div>
              <p className="text-xs text-brand-green/50 mt-2">Le propriétaire bailleur en offre Premium/Pro dispose du module intégré. Un référent (ou un souscripteur de l'offre indépendante) saisit les données manuellement.</p>
            </div>
          </>
        )}
        {offer && (
          <div className="border-t border-brand-soft pt-5 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-semibold text-brand-greenDeep">Offre indépendante « État des lieux »</p>
              <label className="flex items-center gap-2 text-xs font-medium text-brand-greenDeep">
                <input type="checkbox" checked={!!offer.enabled} onChange={(e) => setOffer({ ...offer, enabled: e.target.checked })} className="w-4 h-4 accent-brand-greenDeep" data-testid="edl-offer-enabled" /> Disponible
              </label>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block"><span className="text-xs text-brand-green/60">Nom</span>
                <input value={offer.name || ""} onChange={(e) => setOffer({ ...offer, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1" data-testid="edl-offer-name" /></label>
              <label className="block"><span className="text-xs text-brand-green/60">Prix / mois (€)</span>
                <input type="number" value={offer.price_monthly} onChange={(e) => setOffer({ ...offer, price_monthly: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1" data-testid="edl-offer-monthly" /></label>
              <label className="block"><span className="text-xs text-brand-green/60">Prix / an (€)</span>
                <input type="number" value={offer.price_yearly} onChange={(e) => setOffer({ ...offer, price_yearly: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-brand-soft text-sm mt-1" data-testid="edl-offer-yearly" /></label>
            </div>
            <div>
              <span className="text-xs text-brand-green/60">Services inclus</span>
              <div className="space-y-2 mt-1">
                {(offer.services || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={s} onChange={(e) => setSvc(i, e.target.value)} className="flex-1 px-3 py-2 rounded-xl border border-brand-soft text-sm" />
                    <button type="button" onClick={() => removeSvc(i)} className="text-brand-green/40 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={addSvc} className="text-xs font-semibold text-brand-greenDeep hover:underline">+ Ajouter un service</button>
              </div>
            </div>
            <button onClick={saveOffer} disabled={savingOffer} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="edl-offer-save-btn">
              {savingOffer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer l'offre
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function EdlSettingsManager() {
  const now = new Date();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [invoices, setInvoices] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);
  const [dl, setDl] = useState("");
  useEffect(() => { api.get("/edl/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const loadInv = () => {
    setLoadingInv(true);
    api.get("/admin/edl-commission-invoices", { params: { month } }).then((r) => setInvoices(r.data.invoices || [])).catch(() => toast.error("Chargement impossible")).finally(() => setLoadingInv(false));
  };
  useEffect(() => { loadInv(); /* eslint-disable-next-line */ }, [month]);
  const downloadInv = async (email) => {
    setDl(email);
    try {
      const res = await api.get(`/admin/edl-commission-invoice/${encodeURIComponent(email)}`, { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `commissions-edl-${email}-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Facture indisponible"); }
    finally { setDl(""); }
  };
  const setGrid = (i, patch) => setS((c) => ({ ...c, vetuste_grid: c.vetuste_grid.map((g, idx) => (idx === i ? { ...g, ...patch } : g)) }));
  const setWork = (i, patch) => setS((c) => ({ ...c, works_scale: (c.works_scale || []).map((w, idx) => (idx === i ? { ...w, ...patch } : w)) }));
  const addWork = () => setS((c) => ({ ...c, works_scale: [...(c.works_scale || []), { type: "Nouveau poste", unit_cost: 0 }] }));
  const removeWork = (i) => setS((c) => ({ ...c, works_scale: (c.works_scale || []).filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/edl-settings", {
        fee_entree: Number(s.fee_entree) || 0, fee_sortie: Number(s.fee_sortie) || 0,
        referent_commission_rate: Number(s.referent_commission_rate) || 0,
        vetuste_grid: (s.vetuste_grid || []).map((g) => ({ ...g, lifespan_years: Number(g.lifespan_years) || 0, franchise_years: Number(g.franchise_years) || 0, annual_rate: Number(g.annual_rate) || 0, max_abatement: Number(g.max_abatement) || 0 })),
        works_scale: (s.works_scale || []).map((w) => ({ type: w.type, unit_cost: Number(w.unit_cost) || 0 })),
      });
      toast.success("Paramètres EDL enregistrés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  if (!s) return null;
  return (
    <div data-testid="edl-settings-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2 mb-1"><ClipboardCheck className="w-5 h-5" /> États des lieux — frais & commission du référent</h3>
      <p className="text-sm text-brand-green/55 mb-4">Paramétrez les frais d'EDL, la part de commission du référent et la grille de vétusté (décret n° 2016-382).</p>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 mb-4 flex flex-wrap items-end gap-3">
        <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Frais EDL entrée (€)</span>
          <input type="number" value={s.fee_entree} onChange={(e) => setS({ ...s, fee_entree: e.target.value })} className="admin-inp w-36" data-testid="edl-fee-entree" /></label>
        <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Frais EDL sortie (€)</span>
          <input type="number" value={s.fee_sortie} onChange={(e) => setS({ ...s, fee_sortie: e.target.value })} className="admin-inp w-36" data-testid="edl-fee-sortie" /></label>
        <label className="block"><span className="block text-xs text-brand-green/60 mb-1">Commission référent (%)</span>
          <input type="number" value={s.referent_commission_rate} onChange={(e) => setS({ ...s, referent_commission_rate: e.target.value })} className="admin-inp w-36" data-testid="edl-commission-rate" /></label>
      </div>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 mb-3 overflow-x-auto">
        <p className="text-sm font-semibold text-brand-greenDeep mb-3">Grille de vétusté</p>
        <table className="w-full text-sm">
          <thead className="text-brand-green/55"><tr>
            <th className="text-left py-2 font-semibold">Poste</th>
            <th className="text-left py-2 font-semibold">Durée de vie (ans)</th>
            <th className="text-left py-2 font-semibold">Franchise (ans)</th>
            <th className="text-left py-2 font-semibold">Abattement / an (%)</th>
            <th className="text-left py-2 font-semibold">Abattement max (%)</th>
          </tr></thead>
          <tbody>
            {(s.vetuste_grid || []).map((g, i) => (
              <tr key={i} className="border-t border-brand-soft">
                <td className="py-2 pr-2 text-brand-greenDeep">{g.category}</td>
                <td className="py-2 pr-2"><input type="number" value={g.lifespan_years} onChange={(e) => setGrid(i, { lifespan_years: e.target.value })} className="admin-inp w-24" /></td>
                <td className="py-2 pr-2"><input type="number" value={g.franchise_years} onChange={(e) => setGrid(i, { franchise_years: e.target.value })} className="admin-inp w-24" /></td>
                <td className="py-2 pr-2"><input type="number" value={g.annual_rate} onChange={(e) => setGrid(i, { annual_rate: e.target.value })} className="admin-inp w-24" /></td>
                <td className="py-2 pr-2"><input type="number" value={g.max_abatement} onChange={(e) => setGrid(i, { max_abatement: e.target.value })} className="admin-inp w-24" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 mb-3 overflow-x-auto" data-testid="works-scale-manager">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-brand-greenDeep">Barème des travaux (chiffrage par type de dégradation)</p>
          <button onClick={addWork} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-xs font-semibold" data-testid="add-work-btn"><Wrench className="w-3.5 h-3.5" /> Ajouter un poste</button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-brand-green/55"><tr><th className="text-left py-2 font-semibold">Type de dégradation / travaux</th><th className="text-left py-2 font-semibold">Coût (€)</th><th className="py-2"></th></tr></thead>
          <tbody>
            {(s.works_scale || []).map((w, i) => (
              <tr key={i} className="border-t border-brand-soft" data-testid={`work-row-${i}`}>
                <td className="py-2 pr-2"><input value={w.type} onChange={(e) => setWork(i, { type: e.target.value })} className="admin-inp w-full" /></td>
                <td className="py-2 pr-2"><input type="number" value={w.unit_cost} onChange={(e) => setWork(i, { unit_cost: e.target.value })} className="admin-inp w-28" /></td>
                <td className="py-2 pr-2"><button onClick={() => removeWork(i)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500"><X className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="edl-settings-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
      </button>

      <div className="mt-6" data-testid="edl-invoices">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h4 className="font-semibold text-brand-greenDeep">Facturation mensuelle des commissions référent</h4>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="admin-inp" data-testid="edl-invoice-month" />
        </div>
        <div className="bg-white rounded-2xl border border-brand-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-softer text-brand-green/60"><tr>
              <th className="text-left px-4 py-2 font-semibold">Référent</th>
              <th className="text-left px-4 py-2 font-semibold">EDL réalisés</th>
              <th className="text-left px-4 py-2 font-semibold">Frais encaissés</th>
              <th className="text-left px-4 py-2 font-semibold">Commission due</th>
              <th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {loadingInv ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-brand-green/50"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-brand-green/50">Aucune commission pour ce mois.</td></tr>
              ) : invoices.map((iv) => (
                <tr key={iv.referent_email} className="border-t border-brand-soft" data-testid={`edl-invoice-${iv.referent_email}`}>
                  <td className="px-4 py-2 text-brand-greenDeep">{iv.referent_name}<br /><span className="text-xs text-brand-green/45">{iv.referent_email}</span></td>
                  <td className="px-4 py-2">{iv.count}</td>
                  <td className="px-4 py-2">{iv.fees_total.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-2 font-semibold text-brand-greenDeep">{iv.commission_total.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => downloadInv(iv.referent_email)} disabled={dl === iv.referent_email} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-xs font-semibold" data-testid={`edl-invoice-pdf-${iv.referent_email}`}>
                      {dl === iv.referent_email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


const SOURCE_LABELS = {
  blog: "Blog", simulateur_lmnp_detail: "Simulateur LMNP – détail", simulateur_lmnp_alerte: "Simulateur LMNP – alerte loi",
  outil_quittance: "Générateur de quittance", outil_irl: "Calculateur IRL",
};
const hasConsent = (s) => s.marketing_consent !== false;
const srcLabel = (s) => SOURCE_LABELS[s] || s || "—";
const subSources = (s) => (s.sources && s.sources.length ? s.sources : [s.source || "blog"]);
const SIM_EVENT_LABELS = [["view", "Visites"], ["simulate", "Simuler"], ["detail_email", "Détail e-mail"], ["alert", "Alerte loi"], ["signup", "Créer un compte"], ["share", "Partager"]];

function SimulatorStats() {
  const [counts, setCounts] = useState(null);
  useEffect(() => { api.get("/admin/simulateur-lmnp/stats").then((r) => setCounts(r.data.counts)).catch(() => {}); }, []);
  if (!counts) return null;
  return (
    <div className="mb-6" data-testid="sim-lmnp-stats">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Simulateur LMNP 2027 — clics (anonymes)</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {SIM_EVENT_LABELS.map(([k, label]) => (
          <div key={k} className="bg-white rounded-2xl border border-brand-soft p-4" data-testid={`sim-stat-${k}`}>
            <p className="text-xs text-brand-green/55">{label}</p>
            <p className="font-display text-2xl font-semibold text-brand-greenDeep">{counts[k] || 0}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsletterManager() {
  const [subs, setSubs] = useState([]);
  const [srcFilter, setSrcFilter] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [camps, setCamps] = useState([]);
  const load = () => api.get("/admin/newsletter-subscribers").then((r) => { setSubs(r.data.subscribers || []); setLoaded(true); }).catch(() => setLoaded(true));
  const loadCamps = () => api.get("/admin/newsletter-campaigns").then((r) => setCamps(r.data.campaigns || [])).catch(() => {});
  useEffect(() => { load(); loadCamps(); }, []);
  const allSources = Array.from(new Set(subs.flatMap(subSources)));
  const shown = srcFilter ? subs.filter((s) => subSources(s).includes(srcFilter)) : subs;
  const countBySrc = allSources.map((src) => [src, subs.filter((s) => subSources(s).includes(src)).length]);
  const consented = subs.filter(hasConsent).length;
  const exportCsv = () => {
    if (!shown.length) { toast.error("Aucun inscrit à exporter"); return; }
    const rows = [["email", "source", "date", "derniere_utilisation", "consentement_newsletter", "rappel_irl"], ...shown.map((s) => [s.email, subSources(s).map(srcLabel).join(" | "), (s.created_at || "").slice(0, 10), (s.last_seen_at || s.created_at || "").slice(0, 10), hasConsent(s) ? "oui" : "non", s.irl_reminder_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `newsletter-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Objet et message requis"); return; }
    if (!window.confirm(`Envoyer cette newsletter à ${consented} inscrit(s) ayant accepté la newsletter ?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter-campaign", { subject, message });
      toast.success(`Newsletter envoyée à ${r.data.sent}/${r.data.total} inscrit(s)`);
      setSubject(""); setMessage(""); loadCamps();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSending(false); }
  };
  const inpc = "w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm";
  return (
    <div data-testid="newsletter-manager">
      <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Inbox className="w-5 h-5" /> Newsletter — inscrits & campagnes</h3>
      <div className="bg-white rounded-2xl border border-brand-soft p-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-brand-green/70">{loaded ? `${shown.length} inscrit(s)${srcFilter ? ` · ${srcLabel(srcFilter)}` : " à la newsletter"}` : "Chargement…"}</p>
          <select value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)} className="px-3 py-2 rounded-full border border-brand-soft text-sm text-brand-greenDeep bg-white" data-testid="newsletter-source-filter">
            <option value="">Toutes les sources</option>
            {allSources.map((src) => <option key={src} value={src}>{srcLabel(src)}</option>)}
          </select>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="newsletter-export-csv"><Download className="w-4 h-4" /> Exporter en CSV</button>
        </div>
        {countBySrc.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="newsletter-source-counts">
            {countBySrc.map(([src, n]) => (
              <button key={src} onClick={() => setSrcFilter(srcFilter === src ? "" : src)} className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${srcFilter === src ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "border-brand-soft text-brand-greenDeep hover:bg-brand-softer"}`} data-testid={`newsletter-count-${src}`}>{srcLabel(src)} · {n}</button>
            ))}
            <span className="text-xs px-3 py-1.5 rounded-full bg-brand-softer text-brand-green/70" data-testid="newsletter-consent-count">{consented} avec consentement newsletter</span>
          </div>
        )}
        {shown.length > 0 && (
          <div className="max-h-48 overflow-auto rounded-xl border border-brand-soft divide-y divide-brand-soft">
            {shown.slice(0, 200).map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`newsletter-row-${s.id}`}>
                <span className="text-brand-greenDeep truncate">{s.email}</span>
                <span className="text-xs text-brand-green/45 shrink-0 flex items-center gap-1.5 flex-wrap justify-end">
                  {subSources(s).map((src) => <span key={src} className="px-2 py-0.5 rounded-full bg-brand-softer text-brand-greenDeep">{srcLabel(src)}</span>)}
                  {!hasConsent(s) && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid={`newsletter-noconsent-${s.id}`}>Sans consentement</span>}
                  {s.irl_reminder_at && !s.irl_reminder_sent && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Rappel IRL {s.irl_reminder_at}</span>}
                  <span>{(s.created_at || "").slice(0, 10)}{s.last_seen_at && s.last_seen_at.slice(0, 10) !== (s.created_at || "").slice(0, 10) ? ` · vu ${s.last_seen_at.slice(0, 10)}` : ""}</span>
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-brand-soft pt-5">
          <p className="text-sm font-semibold text-brand-greenDeep mb-2">Envoyer une campagne (via SendGrid)</p>
          <div className="space-y-3">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l'e-mail" className={inpc} data-testid="newsletter-subject" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Votre message… (un paragraphe par ligne)" rows={5} className={inpc} data-testid="newsletter-message" />
            <button onClick={send} disabled={sending || subs.length === 0} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="newsletter-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer à {subs.length} inscrit(s)
            </button>
            <p className="text-xs text-brand-green/45">Les e-mails sont envoyés via SendGrid. Un lien de désinscription est ajouté automatiquement en bas de chaque e-mail.</p>
          </div>
        </div>
        {camps.length > 0 && (
          <div className="border-t border-brand-soft pt-5">
            <p className="text-sm font-semibold text-brand-greenDeep mb-2">Historique des campagnes ({camps.length})</p>
            <div className="max-h-56 overflow-auto rounded-xl border border-brand-soft divide-y divide-brand-soft" data-testid="newsletter-campaigns-history">
              {camps.map((c) => {
                const rate = c.total ? Math.round((c.sent / c.total) * 100) : 0;
                return (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm" data-testid={`campaign-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-brand-greenDeep font-medium truncate">{c.subject}</p>
                      <p className="text-xs text-brand-green/45">{(c.created_at || "").slice(0, 10)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-brand-greenDeep font-semibold">{c.sent}/{c.total}</p>
                      <p className="text-xs text-brand-green/45">{rate}% envoyés</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function Admin() {
  const [artisans, setArtisans] = useState([]);
  const [revenue, setRevenue] = useState(null);

  const load = () => {
    api.get("/admin/artisans").then((r) => setArtisans(r.data)).catch(() => toast.error("Acc\u00e8s admin requis"));
    api.get("/admin/revenue").then((r) => setRevenue(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    await api.put(`/admin/artisans/${id}/${action}`);
    toast.success(action === "approve" ? "Artisan approuv\u00e9" : "Artisan retir\u00e9");
    load();
  };

  const pending = artisans.filter((a) => !a.approved);
  const approved = artisans.filter((a) => a.approved);

  const Card = ({ a }) => (
    <div className="bg-white rounded-2xl p-5 border border-brand-soft">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center"><Wrench className="w-5 h-5 text-brand-greenDeep" /></span>
          <div>
            <h4 className="font-semibold text-brand-greenDeep">{a.company}</h4>
            <p className="text-xs text-brand-green/50 flex items-center gap-2"><span className="px-2 py-0.5 rounded-full bg-brand-lime/30">{a.domain}</span><span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.city}</span></p>
          </div>
        </div>
        <div className="flex gap-2">
          {!a.approved ? (
            <button onClick={() => act(a.id, "approve")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors"><Check className="w-4 h-4" /> Approuver</button>
          ) : (
            <button onClick={() => act(a.id, "reject")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft text-brand-green font-medium text-sm hover:bg-brand-softer transition-colors"><X className="w-4 h-4" /> Retirer</button>
          )}
        </div>
      </div>
      <p className="text-sm text-brand-green/60 mt-3">{a.description}</p>
    </div>
  );

  return (
    <div className="space-y-8">
      <ActivationFunnel />
      <AdminEmailTest />
      <AdminBlogArticles />
      <AdminBlogCovers />
      <AdminOnboardingEmails />
      <AdminHelpVideos />
      {revenue && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-brand-greenDeep rounded-2xl p-6 text-white">
            <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-3"><Wallet className="w-5 h-5 text-brand-lime" /></span>
            <p className="text-3xl font-bold">{revenue.commission.toLocaleString("fr-FR")} €</p>
            <p className="text-sm text-white/60 mt-1">Commissions perçues (10%)</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-brand-soft">
            <span className="w-11 h-11 rounded-xl bg-brand-lime/30 flex items-center justify-center mb-3"><TrendingUp className="w-5 h-5 text-brand-greenDeep" /></span>
            <p className="text-3xl font-bold text-brand-greenDeep">{revenue.gross.toLocaleString("fr-FR")} €</p>
            <p className="text-sm text-brand-green/60 mt-1">Volume traité ({revenue.transactions} paiements)</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-brand-soft">
            <span className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center mb-3"><Users className="w-5 h-5 text-brand-green" /></span>
            <p className="text-3xl font-bold text-brand-greenDeep">{revenue.net_artisans.toLocaleString("fr-FR")} €</p>
            <p className="text-sm text-brand-green/60 mt-1">Reversé aux artisans</p>
          </div>
        </div>
      )}
      <div>
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> En attente de validation ({pending.length})</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {pending.map((a) => <Card key={a.id} a={a} />)}
          {pending.length === 0 && <p className="text-sm text-brand-green/50">Aucun artisan en attente.</p>}
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4">Artisans approuvés ({approved.length})</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {approved.map((a) => <Card key={a.id} a={a} />)}
        </div>
      </div>
      <MenuManager />
      <SupportSettings />
      <ChangePassword />
      <ContactInbox />
      <SimulatorStats />
      <CommunesManager />
      <NewsletterManager />
      <SecuritySettings />
      <AccessManager />
      <SignupProfilesManager />
      <OfferManager />
      <CommissionManager />
      <EdlModuleManager />
      <ReferentAdminManager />
      <ReferentPricingManager />
      <ReferentPaymentPolicyManager />
      <ReferentTestPaymentManager />
      <ReferentRefundsManager />
      <ReferentStatementsManager />
      <EdlSettingsManager />
    </div>
  );
}
